<script>
  /**
   * Hostaway, webhook, Tally and notification settings for one tenant.
   * Secrets are write-only: the server returns only "set" flags and last-4 hints.
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import SecretField from '../SecretField.svelte';

  let { tenant } = $props();

  // ---- Hostaway ----
  let hostaway = $state(null);
  let hostawayForm = $state({ hostaway_account_id: '', client_id: '', client_secret: '' });
  let hostawayBusy = $state(false);
  let hostawayTest = $state(null);

  // ---- Webhook ----
  let webhook = $state(null);
  let webhookBusy = $state(false);

  // ---- Tally ----
  let tally = $state(null);
  let tallyKey = $state('');
  let newForm = $state({ tally_form_id: '', form_role: 'property_knowledge', hostaway_listing_field_key: '', api_key: '' });
  let tallyBusy = $state(false);
  let tallyTest = $state(null);

  // ---- Notifications ----
  let notif = $state(null);
  let notifForm = $state({ channels: [], telegram_chat_id: '', telegram_bot_token: '', slack_webhook_url: '' });
  let notifBusy = $state(false);

  let error = $state(null);

  const enabled = $derived(tenant.integrations_enabled ?? {});

  function fillHostaway(h) {
    hostaway = h;
    hostawayForm = {
      hostaway_account_id: h.hostaway_account_id ?? '',
      client_id: h.client_id ?? '',
      client_secret: '',
    };
  }

  function fillNotif(n) {
    notif = n;
    notifForm = {
      channels: [...n.notification_channels],
      telegram_chat_id: n.telegram_chat_id ?? '',
      telegram_bot_token: '',
      slack_webhook_url: '',
    };
  }

  async function load() {
    error = null;
    try {
      const jobs = [];
      if (enabled.hostaway) jobs.push(api.getTenantHostaway(tenant.id).then(fillHostaway));
      if (enabled.tally) jobs.push(api.getTenantTally(tenant.id).then((t) => (tally = t)));
      if (enabled.str_autoresponder) {
        jobs.push(api.getTenantNotifications(tenant.id).then(fillNotif).catch((e) => {
          if (e.status === 404) notif = null; else throw e;
        }));
      }
      await Promise.all(jobs);
    } catch (e) {
      error = e.message;
    }
  }

  $effect(() => { tenant.id; load(); });

  async function saveHostaway(event) {
    event.preventDefault();
    hostawayBusy = true;
    try {
      fillHostaway(await api.setTenantHostaway(tenant.id, {
        hostaway_account_id: Number(hostawayForm.hostaway_account_id),
        client_id: hostawayForm.client_id.trim(),
        client_secret: hostawayForm.client_secret || null,
      }));
      hostawayTest = null;
      toast.success('Hostaway connection saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      hostawayBusy = false;
    }
  }

  async function testHostaway() {
    hostawayBusy = true;
    try { hostawayTest = await api.testTenantHostaway(tenant.id); }
    catch (e) { toast.error(e.message); }
    finally { hostawayBusy = false; }
  }

  async function checkWebhook() {
    webhookBusy = true;
    try { webhook = await api.getTenantWebhook(tenant.id); }
    catch (e) { toast.error(e.message); }
    finally { webhookBusy = false; }
  }

  async function registerWebhook() {
    webhookBusy = true;
    try {
      webhook = await api.registerTenantWebhook(tenant.id);
      toast.success(webhook.state === 'registered' ? 'Webhook registered' : `Webhook state: ${webhook.state}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      webhookBusy = false;
    }
  }

  async function saveTallyKey(event) {
    event.preventDefault();
    tallyBusy = true;
    try {
      tally = await api.setTenantTallyKey(tenant.id, tallyKey);
      tallyKey = '';
      toast.success('Tally API key replaced');
    } catch (e) {
      toast.error(e.message);
    } finally {
      tallyBusy = false;
    }
  }

  async function addForm(event) {
    event.preventDefault();
    tallyBusy = true;
    try {
      tally = await api.addTenantTallyForm(tenant.id, {
        tally_form_id: newForm.tally_form_id.trim(),
        form_role: newForm.form_role,
        hostaway_listing_field_key: newForm.hostaway_listing_field_key.trim() || null,
        api_key: newForm.api_key || null,
      });
      newForm = { tally_form_id: '', form_role: 'property_knowledge', hostaway_listing_field_key: '', api_key: '' };
      toast.success('Tally form added');
    } catch (e) {
      toast.error(e.message);
    } finally {
      tallyBusy = false;
    }
  }

  async function toggleForm(form) {
    tallyBusy = true;
    try { tally = await api.updateTenantTallyForm(tenant.id, form.id, { is_active: !form.is_active }); }
    catch (e) { toast.error(e.message); }
    finally { tallyBusy = false; }
  }

  async function testTally() {
    tallyBusy = true;
    try { tallyTest = await api.testTenantTally(tenant.id); }
    catch (e) { toast.error(e.message); }
    finally { tallyBusy = false; }
  }

  function toggleChannel(channel) {
    notifForm.channels = notifForm.channels.includes(channel)
      ? notifForm.channels.filter((c) => c !== channel)
      : [...notifForm.channels, channel];
  }

  async function saveNotif(event) {
    event.preventDefault();
    notifBusy = true;
    try {
      const body = {
        notification_channels: notifForm.channels,
        telegram_chat_id: notifForm.telegram_chat_id,
      };
      if (notifForm.telegram_bot_token) body.telegram_bot_token = notifForm.telegram_bot_token;
      if (notifForm.slack_webhook_url) body.slack_webhook_url = notifForm.slack_webhook_url;
      fillNotif(await api.setTenantNotifications(tenant.id, body));
      toast.success('Notification settings saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      notifBusy = false;
    }
  }

  async function clearSecret(field) {
    notifBusy = true;
    try {
      fillNotif(await api.setTenantNotifications(tenant.id, { clear: [field] }));
      toast.success('Removed');
    } catch (e) {
      toast.error(e.message);
    } finally {
      notifBusy = false;
    }
  }

  async function testNotif(channel) {
    notifBusy = true;
    try {
      const r = await api.testTenantNotification(tenant.id, channel);
      if (r.success) toast.success(`Test ${channel} notification sent`);
      else toast.error(`Test ${channel} failed: ${r.error_message ?? 'unknown error'}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      notifBusy = false;
    }
  }

  const webhookTone = {
    registered: ['var(--p-success-bg)', 'var(--p-success)'],
    mismatch: ['var(--p-warning-bg)', 'var(--p-warning)'],
    none: ['var(--p-danger-bg)', 'var(--p-danger)'],
  };
</script>

{#snippet result(r)}
  {#if r}
    <span class="p-badge" style="background-color: var({r.ok ? '--p-success-bg' : '--p-danger-bg'});
                                 color: var({r.ok ? '--p-success' : '--p-danger'})">
      {r.ok ? '✓' : '✗'} {r.message}
    </span>
  {/if}
{/snippet}

{#if error}
  <p class="p-card mb-4 p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{/if}

<div class="space-y-6">
  {#if enabled.hostaway && hostaway}
    <section class="p-card p-4">
      <h2 class="text-sm font-semibold">Hostaway</h2>
      <p class="mb-4 text-xs p-faint">
        Syncs properties and conversations, and delivers approved replies. Connecting an account
        never turns on real sends — that is a separate switch.
      </p>
      <form class="space-y-4" onsubmit={saveHostaway}>
        <div class="grid gap-4 sm:grid-cols-3">
          <div>
            <label for="ha-account" class="text-xs font-medium p-muted">Hostaway account ID</label>
            <input id="ha-account" class="p-input mt-1" type="number" min="1" required
                   bind:value={hostawayForm.hostaway_account_id} />
          </div>
          <div>
            <label for="ha-client" class="text-xs font-medium p-muted">Client ID</label>
            <input id="ha-client" class="p-input mt-1" required bind:value={hostawayForm.client_id} />
          </div>
          <SecretField id="ha-secret" label="Client secret" required
                       status={{ set: hostaway.client_secret_set, last4: hostaway.client_secret_last4,
                                 readable: hostaway.client_secret_readable }}
                       bind:value={hostawayForm.client_secret} />
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="text-xs p-faint">
            {#if hostaway.configured}
              {hostaway.is_active ? 'Active' : 'Inactive'}
              · real sends {hostaway.allow_real_sends ? 'ON' : 'off'}
              {#if hostaway.configured && !hostaway.client_secret_encrypted}· <span style="color: var(--p-warning)">stored unencrypted</span>{/if}
            {:else}
              Not connected.
            {/if}
          </div>
          <div class="flex items-center gap-2">
            {@render result(hostawayTest)}
            <button type="button" class="p-btn p-btn-quiet" disabled={hostawayBusy || !hostaway.configured}
                    onclick={testHostaway}>Test connection</button>
            <button type="submit" class="p-btn p-btn-primary" disabled={hostawayBusy}>
              {hostaway.configured ? 'Save' : 'Connect'}
            </button>
          </div>
        </div>
      </form>
    </section>

    <section class="p-card p-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-sm font-semibold">Real-time webhook</h2>
          <p class="text-xs p-faint">Hostaway pushes new guest messages here as they arrive.</p>
        </div>
        <div class="flex gap-2">
          <button type="button" class="p-btn p-btn-quiet" disabled={webhookBusy || !hostaway.configured}
                  onclick={checkWebhook}>Check</button>
          <button type="button" class="p-btn p-btn-primary"
                  disabled={webhookBusy || !hostaway.configured || webhook?.state === 'not_configured'}
                  onclick={registerWebhook}>Register</button>
        </div>
      </div>
      {#if webhook}
        {@const tone = webhookTone[webhook.state] ?? ['var(--p-bg-inset)', 'var(--p-text-muted)']}
        <div class="mt-3 rounded-lg px-3 py-2 text-sm" style="background-color: {tone[0]}; color: {tone[1]}">
          <strong>{webhook.state}</strong>{#if webhook.mode === 'mock'} (mock server — not real Hostaway){/if}
          — {webhook.detail}
          {#if webhook.expected_url}<p class="mt-1 text-xs">Expected URL: <code>{webhook.expected_url}</code></p>{/if}
        </div>
        {#if webhook.entries?.length}
          <ul class="mt-2 space-y-1 text-xs p-muted">
            {#each webhook.entries as entry}
              <li>
                <code>{entry.url}</code> · {entry.is_enabled ? 'enabled' : 'disabled'}
                {#if entry.is_ours}· ours{#if entry.login_matches === false} (login differs){/if}{/if}
              </li>
            {/each}
          </ul>
        {/if}
      {/if}
    </section>
  {/if}

  {#if enabled.tally && tally}
    <section class="p-card p-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-sm font-semibold">Tally</h2>
          <p class="text-xs p-faint">
            Owner-submitted property facts. One workspace API key, one row per form.
            {#if tally.unmatched_submissions}· {tally.unmatched_submissions} unmatched submission(s){/if}
          </p>
        </div>
        <div class="flex items-center gap-2">
          {@render result(tallyTest)}
          <button type="button" class="p-btn p-btn-quiet" disabled={tallyBusy || !tally.api_key_set}
                  onclick={testTally}>Test connection</button>
        </div>
      </div>

      {#if tally.forms.length}
        <table class="mt-3 w-full text-left text-sm">
          <thead>
            <tr class="text-xs p-muted">
              <th class="py-1">Form</th><th>Role</th><th>Listing field</th><th>Last sync</th><th></th>
            </tr>
          </thead>
          <tbody>
            {#each tally.forms as form (form.id)}
              <tr style="border-top: 1px solid var(--p-border)" class:opacity-60={!form.is_active}>
                <td class="py-2"><code>{form.tally_form_id}</code></td>
                <td>{form.form_role}</td>
                <td>{form.hostaway_listing_field_key ?? '—'}</td>
                <td class="text-xs p-muted">
                  {form.last_synced_at ? new Date(form.last_synced_at).toLocaleString() : 'never'}
                </td>
                <td class="text-right">
                  <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs" disabled={tallyBusy}
                          onclick={() => toggleForm(form)}>{form.is_active ? 'Disable' : 'Enable'}</button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>

        <form class="mt-4 flex items-end gap-2" onsubmit={saveTallyKey}>
          <div class="flex-1">
            <SecretField id="tally-key" label="Workspace API key"
                         status={{ set: tally.api_key_set, last4: tally.api_key_last4, readable: tally.api_key_readable }}
                         bind:value={tallyKey} placeholder="tly-…" />
          </div>
          <button type="submit" class="p-btn p-btn-quiet" disabled={tallyBusy || !tallyKey}>Replace key</button>
        </form>
      {/if}

      <form class="mt-4 space-y-3 rounded-lg border p-3" style="border-color: var(--p-border)" onsubmit={addForm}>
        <p class="text-xs font-semibold">Add a form</p>
        <div class="grid gap-3 sm:grid-cols-3">
          <div>
            <label for="tf-id" class="text-xs font-medium p-muted">Form ID</label>
            <input id="tf-id" class="p-input mt-1" required bind:value={newForm.tally_form_id} />
          </div>
          <div>
            <label for="tf-role" class="text-xs font-medium p-muted">Role</label>
            <select id="tf-role" class="p-input mt-1" bind:value={newForm.form_role}>
              <option value="property_knowledge">Property facts</option>
              <option value="owner_info">Owner / contact info</option>
            </select>
          </div>
          <div>
            <label for="tf-field" class="text-xs font-medium p-muted">Hidden listing-id field (optional)</label>
            <input id="tf-field" class="p-input mt-1" bind:value={newForm.hostaway_listing_field_key} />
          </div>
        </div>
        {#if !tally.api_key_set}
          <SecretField id="tf-key" label="Workspace API key" required status={{ set: false }}
                       bind:value={newForm.api_key} placeholder="tly-…" />
        {/if}
        <div class="flex justify-end">
          <button type="submit" class="p-btn p-btn-primary" disabled={tallyBusy}>Add form</button>
        </div>
      </form>
    </section>
  {/if}

  {#if enabled.str_autoresponder}
    <section class="p-card p-4">
      <h2 class="text-sm font-semibold">Host notifications</h2>
      {#if !notif}
        <p class="mt-2 text-sm p-muted">
          This tenant has no auto-responder configuration yet — create it from the Readiness tab.
        </p>
      {:else}
        <p class="mb-4 text-xs p-faint">Where drafts and escalations are sent for review.</p>
        <form class="space-y-4" onsubmit={saveNotif}>
          <fieldset>
            <legend class="text-xs font-medium p-muted">Channels</legend>
            <div class="mt-1 flex flex-wrap gap-4 text-sm">
              {#each ['telegram', 'email', 'slack', 'sms'] as channel}
                <label class="flex items-center gap-1">
                  <input type="checkbox" checked={notifForm.channels.includes(channel)}
                         onchange={() => toggleChannel(channel)} /> {channel}
                </label>
              {/each}
            </div>
          </fieldset>
          <div class="grid gap-4 sm:grid-cols-2">
            <div>
              <label for="n-chat" class="text-xs font-medium p-muted">Telegram chat id(s)</label>
              <input id="n-chat" class="p-input mt-1" bind:value={notifForm.telegram_chat_id}
                     placeholder="-1001234567890, 12345678" />
              {#if !notifForm.telegram_chat_id && notif.env_defaults.telegram_default_chat}
                <p class="mt-1 text-xs" style="color: var(--p-warning)">
                  Empty — alerts go to the server's shared default chat.
                </p>
              {/if}
            </div>
            <div>
              <SecretField id="n-bot" label="Own Telegram bot token (optional)"
                           status={notif.telegram_bot_token} bind:value={notifForm.telegram_bot_token} />
              <p class="mt-1 text-xs p-faint">
                Stored encrypted but not yet used — every tenant currently goes through the shared STRAO bot.
                {#if notif.telegram_bot_token.set}
                  <button type="button" class="underline" onclick={() => clearSecret('telegram_bot_token')}>Remove</button>
                {/if}
              </p>
            </div>
            <div class="sm:col-span-2">
              <SecretField id="n-slack" label="Slack incoming-webhook URL (optional)"
                           status={notif.slack_webhook_url} bind:value={notifForm.slack_webhook_url}
                           placeholder="https://hooks.slack.com/services/…" />
              <p class="mt-1 text-xs p-faint">
                Overrides the server default{notif.env_defaults.slack ? '' : ' (none set)'}.
                {#if notif.slack_webhook_url.set}
                  <button type="button" class="underline" onclick={() => clearSecret('slack_webhook_url')}>Remove</button>
                {/if}
              </p>
            </div>
          </div>
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex gap-1">
              {#each notif.notification_channels as channel}
                <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs" disabled={notifBusy}
                        onclick={() => testNotif(channel)}>Test {channel}</button>
              {/each}
            </div>
            <button type="submit" class="p-btn p-btn-primary" disabled={notifBusy}>Save notifications</button>
          </div>
        </form>
      {/if}
    </section>
  {/if}
</div>
